import axios from "axios";
import * as React from "react";
import type { FeatureCollection, Feature, Geometry } from "geojson";
import { API_ENDPOINT } from "../configurations/global.config";
import { AuthContext } from "../contexts/user-context/user-context";

// Default cache TTL: 30 minutes (in milliseconds)
const DEFAULT_CACHE_TTL = 30 * 60 * 1000;

interface CachedData {
  data: FeatureCollection<Geometry>;
  timestamp: number;
}

/**
 * Robustly normalize anything DRF/DRF-GIS might return into a FeatureCollection.
 * Supports:
 * - FeatureCollection (already)
 * - { results: FeatureCollection }
 * - { results: Feature[] }
 * - Feature[]
 */
function normalizeToFC(input: any): FeatureCollection<Geometry> | null {
  if (!input) return null;

  // Already a FC
  if (input.type === "FeatureCollection" && Array.isArray(input.features)) {
    return input as FeatureCollection<Geometry>;
  }

  // Paginated or nested under results
  if (input.results) {
    const r = input.results;
    if (r?.type === "FeatureCollection" && Array.isArray(r.features)) {
      return r as FeatureCollection<Geometry>;
    }
    if (Array.isArray(r) && r.length && r[0].type === "Feature") {
      return { type: "FeatureCollection", features: r as Feature<Geometry, any>[] };
    }
  }

  // Plain array of features
  if (Array.isArray(input) && input.length && input[0].type === "Feature") {
    return { type: "FeatureCollection", features: input as Feature<Geometry, any>[] };
  }

  // Single feature
  if (input.type === "Feature" && input.geometry) {
    return { type: "FeatureCollection", features: [input as Feature<Geometry, any>] };
  }

  return null;
}

/**
 * Get cached data from localStorage if it exists and hasn't expired
 */
function getCachedData(key: string, ttl: number): FeatureCollection<Geometry> | null {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const cachedData: CachedData = JSON.parse(cached);
    const now = Date.now();

    // Check if cache has expired
    if (now - cachedData.timestamp > ttl) {
      localStorage.removeItem(key);
      return null;
    }

    return cachedData.data;
  } catch (error) {
    console.error("Error reading from cache:", error);
    return null;
  }
}

/**
 * Store data in localStorage with current timestamp
 */
function setCachedData(key: string, data: FeatureCollection<Geometry>): void {
  try {
    const cachedData: CachedData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cachedData));
  } catch (error) {
    console.error("Error writing to cache:", error);
  }
}

export function useGeo(opts?: {
  iso?: string;           // default "LBN"
  withADM2?: boolean;     // default true
  withBorder?: boolean;   // default true
  cacheTTL?: number;      // cache time-to-live in milliseconds, default 30 minutes
}) {
  const iso = (opts?.iso ?? "LBN").toUpperCase();
  const withADM2 = opts?.withADM2 ?? true;
  const withBorder = opts?.withBorder ?? true;
  const cacheTTL = opts?.cacheTTL ?? DEFAULT_CACHE_TTL;

  const { config } = React.useContext(AuthContext);

  const [borderFC, setBorderFC] = React.useState<FeatureCollection<Geometry> | null>(null);
  const [adm2FC, setAdm2FC] = React.useState<FeatureCollection<Geometry> | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);

  // Cache keys based on ISO code
  const borderCacheKey = `geo_border_${iso}`;
  const adm2CacheKey = `geo_adm2_${iso}`;

  const fetchBorder = React.useCallback(async () => {
    // Check cache first
    const cached = getCachedData(borderCacheKey, cacheTTL);
    if (cached) {
      return cached;
    }

    // Fetch from API
    const url = `${API_ENDPOINT}/api/v1/country-borders/`;
    const { data } = await axios.get(url, { ...config, params: { iso } });
    const normalized = normalizeToFC(data);
    
    // Cache the successful response
    if (normalized) {
      setCachedData(borderCacheKey, normalized);
    }
    
    return normalized;
  }, [config, iso, borderCacheKey, cacheTTL]);

  const fetchADM2 = React.useCallback(async () => {
    // Check cache first
    const cached = getCachedData(adm2CacheKey, cacheTTL);
    if (cached) {
      return cached;
    }

    // Fetch from API
    const url = `${API_ENDPOINT}/api/v1/admin/adm2/`;
    const { data } = await axios.get(url, { ...config, params: { iso } });
    console.log(data);
    const normalized = normalizeToFC(data);
    
    // Cache the successful response
    if (normalized) {
      setCachedData(adm2CacheKey, normalized);
    }
    
    return normalized;
  }, [config, iso, adm2CacheKey, cacheTTL]);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [border, adm2] = await Promise.all([
        withBorder ? fetchBorder() : Promise.resolve(null),
        withADM2 ? fetchADM2() : Promise.resolve(null),
      ]);
      setBorderFC(border);
      setAdm2FC(adm2);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [fetchBorder, fetchADM2, withBorder, withADM2]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  return {
    borderFC,
    adm2FC,
    loading,
    error,
    reload,
  };
}
